import React, { useEffect, useCallback, useRef } from 'react';

export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  metaKey?: boolean;
  preventDefault?: boolean;
  description: string;
  action: () => void;
}

interface UseKeyboardShortcutsOptions {
  enabled?: boolean;
  target?: HTMLElement | Document;
}

export const useKeyboardShortcuts = (
  shortcuts: KeyboardShortcut[],
  options: UseKeyboardShortcutsOptions = {}
) => {
  const { enabled = true, target = document } = options;
  const shortcutsRef = useRef(shortcuts);
  
  // Update shortcuts ref when shortcuts change
  shortcutsRef.current = shortcuts;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Don't trigger shortcuts when user is typing in inputs
    const activeElement = document.activeElement;
    if (activeElement && (
      activeElement.tagName === 'INPUT' ||
      activeElement.tagName === 'TEXTAREA' ||
      activeElement.tagName === 'SELECT' ||
      activeElement.getAttribute('contenteditable') === 'true'
    )) {
      // Allow only specific shortcuts in input fields (like Escape)
      const allowedInInputs = ['Escape'];
      if (!allowedInInputs.includes(event.key)) {
        return;
      }
    }

    for (const shortcut of shortcutsRef.current) {
      const matches = (
        event.key.toLowerCase() === shortcut.key.toLowerCase() &&
        !!event.ctrlKey === !!shortcut.ctrlKey &&
        !!event.altKey === !!shortcut.altKey &&
        !!event.shiftKey === !!shortcut.shiftKey &&
        !!event.metaKey === !!shortcut.metaKey
      );

      if (matches) {
        if (shortcut.preventDefault !== false) {
          event.preventDefault();
        }
        shortcut.action();
        break; // Only execute the first matching shortcut
      }
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    target.addEventListener('keydown', handleKeyDown);
    
    return () => {
      target.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, target, handleKeyDown]);

  return {
    shortcuts: shortcutsRef.current
  };
};

// Export types for use in components
export type { KeyboardShortcut };

// Hook specifically for schedule-related keyboard shortcuts
export const useScheduleKeyboardShortcuts = (
  onNewSchedule: () => void,
  onNavigateLeft?: () => void,
  onNavigateRight?: () => void,
  onNavigateUp?: () => void,
  onNavigateDown?: () => void,
  onEscape?: () => void,
  onToggleFullscreen?: () => void,
  onRefresh?: () => void
) => {
  const shortcuts: KeyboardShortcut[] = [
    // Create new schedule
    {
      key: 'n',
      ctrlKey: true,
      description: 'Create new schedule',
      action: onNewSchedule
    },
    
    // Navigation shortcuts
    {
      key: 'ArrowLeft',
      description: 'Navigate left/previous',
      action: onNavigateLeft || (() => {}),
      preventDefault: !!onNavigateLeft
    },
    {
      key: 'ArrowRight', 
      description: 'Navigate right/next',
      action: onNavigateRight || (() => {}),
      preventDefault: !!onNavigateRight
    },
    {
      key: 'ArrowUp',
      description: 'Navigate up',
      action: onNavigateUp || (() => {}),
      preventDefault: !!onNavigateUp
    },
    {
      key: 'ArrowDown',
      description: 'Navigate down', 
      action: onNavigateDown || (() => {}),
      preventDefault: !!onNavigateDown
    },
    
    // Escape to close modals/cancel actions
    {
      key: 'Escape',
      description: 'Close modal or cancel action',
      action: onEscape || (() => {}),
      preventDefault: false // Let other escape handlers work too
    },
    
    // Fullscreen toggle
    {
      key: 'f',
      ctrlKey: true,
      description: 'Toggle fullscreen view',
      action: onToggleFullscreen || (() => {}),
      preventDefault: !!onToggleFullscreen
    },
    
    // Refresh
    {
      key: 'r',
      ctrlKey: true,
      description: 'Refresh schedule data',
      action: onRefresh || (() => {}),
      preventDefault: !!onRefresh
    },
    
    // Help overlay
    {
      key: '?',
      shiftKey: true,
      description: 'Show keyboard shortcuts help',
      action: () => {
        // This will be handled by the help overlay component
      },
      preventDefault: false
    }
  ].filter(shortcut => {
    // Filter out shortcuts with empty action functions
    const actionStr = shortcut.action.toString();
    return !actionStr.includes('=> {}') || actionStr.includes('help overlay');
  });

  return useKeyboardShortcuts(shortcuts);
};

// Utility function to format keyboard shortcuts for display
export const formatKeyboardShortcut = (shortcut: KeyboardShortcut): string => {
  const parts: string[] = [];
  
  if (shortcut.ctrlKey) parts.push('Ctrl');
  if (shortcut.metaKey) parts.push('Cmd');
  if (shortcut.altKey) parts.push('Alt');
  if (shortcut.shiftKey) parts.push('Shift');
  
  // Format special keys
  let key = shortcut.key;
  if (key === 'ArrowLeft') key = '←';
  if (key === 'ArrowRight') key = '→';
  if (key === 'ArrowUp') key = '↑';
  if (key === 'ArrowDown') key = '↓';
  if (key === 'Escape') key = 'Esc';
  if (key === ' ') key = 'Space';
  
  parts.push(key);
  
  return parts.join(' + ');
};